import React, { ReactNode } from "react";

interface ActionButton {
  label: string;
  onClick: () => void;
  backgroundColor?: string;
  disabled?: boolean;
  showNotification?: boolean; // Show a notification badge on the button
}

interface ActionPanelProps {
  leftButtons?: ActionButton[];
  rightButtons?: ActionButton[];
  activeContent?: ReactNode;
  actionCounter?: ReactNode; // Counter to display to the right of action buttons
}

export default function ActionPanel({ leftButtons = [], rightButtons = [], activeContent, actionCounter }: ActionPanelProps) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div style={{ display: "flex", alignItems: "stretch", gap: "0.5rem", justifyContent: "space-between" }}>
        {/* Action counter and left actions */}
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "stretch" }}>
          {/* Action counter - styled as transparent button */}
          {actionCounter && (
            <>
              <div style={{
                padding: "0.5rem 1rem",
                backgroundColor: "rgba(0, 123, 255, 0.1)",
                border: "1px solid rgba(0, 123, 255, 0.3)",
                borderRadius: "4px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                gap: "0.25rem",
                cursor: "default",
                opacity: 1,
                alignSelf: "stretch"
              }}>
                {actionCounter}
              </div>
              {leftButtons.length > 0 && (
                <div style={{
                  width: "1px",
                  height: "100%",
                  backgroundColor: "rgba(0, 0, 0, 0.2)",
                  margin: "0 0.25rem",
                  alignSelf: "stretch"
                }} />
              )}
            </>
          )}
          {/* Left actions */}
          {leftButtons.length > 0 && (
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "stretch" }}>
            {leftButtons.map((button, index) => (
              <div key={index} style={{ position: "relative", display: "flex", alignSelf: "stretch" }}>
                <button
                  onClick={button.onClick}
                  disabled={button.disabled}
                  data-testid={`action-button-${button.label.toLowerCase().replace(/\s+/g, '-')}`}
                  style={{
                    padding: "0.5rem 1rem",
                    fontSize: "0.9rem",
                    cursor: button.disabled ? "not-allowed" : "pointer",
                    backgroundColor: button.disabled ? "#6c757d" : (button.backgroundColor || "#007bff"),
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    opacity: button.disabled ? 0.6 : 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    gap: "0.25rem",
                    height: "100%",
                    width: "100%"
                  }}
                >
                  {(() => {
                    const lines = button.label.split('\n');
                    const actionName = lines[0];
                    const additionalLines = lines.slice(1);
                    const hasMultipleAdditional = additionalLines.length > 1;
                    const costLineIndex = additionalLines.findIndex(line => line.toLowerCase().includes('cost'));
                    
                    return (
                      <>
                        <span style={{ fontSize: "1.2rem", fontWeight: "600" }}>{actionName}</span>
                        {additionalLines.length > 0 && (
                          <>
                            <hr style={{
                              width: "100%",
                              margin: 0,
                              border: "none",
                              borderTop: "1px solid rgba(255, 255, 255, 0.5)",
                              opacity: 0.7
                            }} />
                            {hasMultipleAdditional ? (
                              <div style={{ 
                                display: "flex", 
                                justifyContent: "space-between", 
                                alignItems: "center",
                                width: "100%",
                                fontSize: "0.85rem",
                                fontWeight: "400",
                                gap: "1rem"
                              }}>
                                <span>{additionalLines.filter((_, i) => i !== costLineIndex).join(' ')}</span>
                                {costLineIndex >= 0 && <span>{additionalLines[costLineIndex]}</span>}
                              </div>
                            ) : (
                              <span style={{ fontSize: "0.85rem", fontWeight: "400" }}>{additionalLines[0]}</span>
                            )}
                          </>
                        )}
                      </>
                    );
                  })()}
                </button>
                {button.showNotification && (
                  <span
                    style={{
                      position: "absolute",
                      top: "-8px",
                      right: "-8px",
                      backgroundColor: "#dc3545",
                      color: "white",
                      borderRadius: "50%",
                      width: "20px",
                      height: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      fontWeight: "bold",
                      border: "2px solid white",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                    }}
                    title="New updates available"
                  >
                    !
                  </span>
                )}
              </div>
            ))}
            </div>
          )}
        </div>
        
        {/* Right actions */}
        {rightButtons.length > 0 && (
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "stretch" }}>
            {rightButtons.map((button, index) => (
              <div key={index} style={{ position: "relative", display: "flex", alignSelf: "stretch" }}>
                <button
                  onClick={button.onClick}
                  disabled={button.disabled}
                  data-testid={`action-button-${button.label.toLowerCase().replace(/\s+/g, '-')}`}
                  style={{
                    padding: "0.5rem 1rem",
                    fontSize: "0.9rem",
                    cursor: button.disabled ? "not-allowed" : "pointer",
                    backgroundColor: button.disabled ? "#6c757d" : (button.backgroundColor || "#17a2b8"),
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    opacity: button.disabled ? 0.6 : 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    gap: "0.25rem",
                    height: "100%",
                    width: "100%"
                  }}
                >
                  <span style={{ fontSize: "1.2rem", fontWeight: "600" }}>{button.label}</span>
                </button>
                {button.showNotification && (
                  <span
                    style={{
                      position: "absolute",
                      top: "-8px",
                      right: "-8px",
                      backgroundColor: "#dc3545",
                      color: "white",
                      borderRadius: "50%",
                      width: "20px",
                      height: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      fontWeight: "bold",
                      border: "2px solid white",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                    }}
                    title="New updates available"
                  >
                    !
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {activeContent && activeContent}
    </div>
  );
}

